import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { User } from "oidc-client-ts";
import { initAuth, login, logout, subscribe, fetchAuthConfig } from "../lib/auth";

interface AuthState {
  ready: boolean;
  enabled: boolean;
  user: User | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [ready, setReady] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchAuthConfig();
        if (cancelled) return;
        setEnabled(cfg.enabled);
        if (cfg.enabled) {
          const { user: u } = await initAuth();
          if (cancelled) return;
          setUser(u);
        }
      } catch (err) {
        console.warn("Auth init failed:", err);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    const off = subscribe((u) => setUser(u));
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ready, enabled, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
